import React, { useMemo, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Search, Users, TrendingUp, UserPlus, Cake, ChevronRight, Crown, Sparkles, Repeat, Moon, Mail, Phone, Calendar, MapPin, Star, Download, X, ChevronDown } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { useCRM, Customer, CustomerTag, Sex, tagMeta, getCustomerMetrics } from './CRMContext';
import { useBranches } from './BranchesContext';
import { CustomerProfilePanel } from './CustomerProfilePanel';
type FilterTag = 'all' | CustomerTag;
export function CRMPage() {
  const {
    customers
  } = useCRM();
  const {
    branches
  } = useBranches();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterTag>('all');
  const [branchId, setBranchId] = useState<string>('all');
  // Customers visible at the currently-scoped branch (any order at that branch).
  // When 'all', show everyone. Per-customer metrics stay as lifetime totals so
  // top-spender rankings stay meaningful.
  const branchScopedCustomers = useMemo(() => {
    if (branchId === 'all') return customers;
    return customers.filter(c => c.orders.some(o => o.branchId === branchId));
  }, [customers, branchId]);
  const [sortKey, setSortKey] = useState<'joined' | 'firstName' | 'lastName' | 'spend'>('joined');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Stats — scoped to the current branch selection
  const stats = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const total = branchScopedCustomers.length;
    const newThisWeek = branchScopedCustomers.filter(c => new Date(c.joined).getTime() >= sevenDaysAgo).length;
    const withRepeatOrders = branchScopedCustomers.filter(c => c.orders.filter(o => o.status === 'completed').length >= 2).length;
    const repeatRate = total > 0 ? withRepeatOrders / total * 100 : 0;
    const totalLTV = branchScopedCustomers.reduce((s, c) => s + c.orders.filter(o => o.status === 'completed').reduce((s2, o) => s2 + o.total, 0), 0);
    const avgLTV = total > 0 ? totalLTV / total : 0;
    const thisMonth = new Date().getMonth();
    const birthdaysThisMonth = branchScopedCustomers.filter(c => {
      if (!c.birthDate) return false;
      return new Date(c.birthDate).getMonth() === thisMonth;
    }).length;
    return {
      total,
      newThisWeek,
      repeatRate,
      avgLTV,
      birthdaysThisMonth
    };
  }, [branchScopedCustomers]);
  // Filtered + sorted
  const filtered = useMemo(() => {
    let list = branchScopedCustomers;
    if (filter !== 'all') {
      list = list.filter(c => c.tags.includes(filter));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => c.firstName.toLowerCase().includes(q) || c.lastName.toLowerCase().includes(q) || c.phone.includes(q) || c.email && c.email.toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'joined') cmp = a.joined.localeCompare(b.joined);else if (sortKey === 'firstName') cmp = a.firstName.localeCompare(b.firstName);else if (sortKey === 'lastName') cmp = a.lastName.localeCompare(b.lastName);else if (sortKey === 'spend') {
        const aSpend = getCustomerMetrics(a).lifetimeSpend;
        const bSpend = getCustomerMetrics(b).lifetimeSpend;
        cmp = aSpend - bSpend;
      }
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [branchScopedCustomers, filter, search, sortKey, sortAsc]);
  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);else {
      setSortKey(key);
      setSortAsc(key === 'firstName' || key === 'lastName');
    }
  };
  const filterPills: {
    key: FilterTag;
    label: string;
    count: number;
  }[] = useMemo(() => [{
    key: 'all',
    label: 'All',
    count: branchScopedCustomers.length
  }, {
    key: 'vip',
    label: 'VIP',
    count: branchScopedCustomers.filter(c => c.tags.includes('vip')).length
  }, {
    key: 'regular',
    label: 'Regulars',
    count: branchScopedCustomers.filter(c => c.tags.includes('regular')).length
  }, {
    key: 'new',
    label: 'New',
    count: branchScopedCustomers.filter(c => c.tags.includes('new')).length
  }, {
    key: 'dormant',
    label: 'Dormant',
    count: branchScopedCustomers.filter(c => c.tags.includes('dormant')).length
  }, {
    key: 'birthday-month',
    label: 'Birthdays',
    count: branchScopedCustomers.filter(c => c.tags.includes('birthday-month')).length
  }], [branchScopedCustomers]);
  const selectedCustomer = selectedId !== null ? customers.find(c => c.id === selectedId) : null;
  const exportCSV = () => {
    const headers = ['First Name', 'Last Name', 'Sex', 'Phone', 'Email', 'Birth Date', 'Joined', 'Tags', 'Total Orders', 'Lifetime Spend (ETB)'];
    const rows = branchScopedCustomers.map(c => {
      const m = getCustomerMetrics(c);
      return [c.firstName, c.lastName, c.sex, c.phone, c.email || '', c.birthDate || '', c.joined, c.tags.join('|'), m.totalOrders.toString(), m.lifetimeSpend.toFixed(2)];
    });
    const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-1277">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-1278">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-1279">
          <Menu size={24} data-id="element-1280" />
        </button>
        <span className="font-bold" data-id="element-1281">CRM</span>
        <div className="w-6" data-id="element-1282" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-1283" />

      <main className="flex-1 overflow-y-auto" data-id="element-1284">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-id="element-1285">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 flex-wrap" data-id="element-1286">
            <div data-id="element-1287">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-1288">CRM</h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1289">
                Customer relationships, history and outreach
                {branchId !== 'all' && <span className="ml-1 text-gray-400" data-id="element-1290">
                    · showing customers who ordered at{' '}
                    <span className="font-semibold text-gray-700" data-id="element-1291">
                      {branches.find(b => b.id === branchId)?.name || 'this branch'}
                    </span>
                  </span>}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-id="element-1292">
              <div className="relative" data-id="element-1293">
                <select value={branchId} onChange={e => setBranchId(e.target.value)} className="appearance-none pl-9 pr-8 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 cursor-pointer" data-id="element-1294">
                  <option value="all" data-id="element-1295">All branches</option>
                  {branches.filter(b => b.active).map(b => <option key={b.id} value={b.id} data-id="element-1296">
                        {b.name}
                      </option>)}
                </select>
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-1297" />
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-1298" />
              </div>
              <button onClick={exportCSV} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors" data-id="element-1299">
                <Download size={14} data-id="element-1300" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-id="element-1301">
            <StatCard icon={<Users size={16} data-id="element-1303" />} label="Customers" value={stats.total.toString()} tint="orange" data-id="element-1302" />
            <StatCard icon={<UserPlus size={16} data-id="element-1305" />} label="New this week" value={stats.newThisWeek.toString()} tint="blue" data-id="element-1304" />
            <StatCard icon={<Repeat size={16} data-id="element-1307" />} label="Repeat rate" value={`${stats.repeatRate.toFixed(0)}%`} tint="emerald" data-id="element-1306" />
            <StatCard icon={<TrendingUp size={16} data-id="element-1309" />} label="Avg lifetime" value={`ETB ${Math.round(stats.avgLTV).toLocaleString()}`} tint="purple" data-id="element-1308" />
            <StatCard icon={<Cake size={16} data-id="element-1311" />} label="Birthdays this month" value={stats.birthdaysThisMonth.toString()} tint="pink" data-id="element-1310" />
          </div>

          {/* Search + Filter */}
          <div className="flex flex-col gap-3" data-id="element-1312">
            <div className="relative max-w-md" data-id="element-1313">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1314" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone or email..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-1315" />
            </div>

            {/* Filter chips */}
            <div className="flex items-center gap-2 flex-wrap" data-id="element-1316">
              {filterPills.map(p => {
              const isActive = filter === p.key;
              return <button key={p.key} onClick={() => setFilter(p.key)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${isActive ? 'bg-[#0f172a] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'}`} data-id="element-1317">
                    {p.label}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] tabular-nums ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1318">
                      {p.count}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1319">
            <div className="overflow-x-auto" data-id="element-1320">
              <table className="w-full text-sm" data-id="element-1321">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1322">
                  <tr data-id="element-1323">
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider w-10" data-id="element-1324">
                      #
                    </th>
                    <SortableTh label="Customer" sortKey="firstName" currentKey={sortKey} asc={sortAsc} onClick={() => toggleSort('firstName')} data-id="element-1325" />
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1326">
                      Sex
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1327">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1328">
                      Birth date
                    </th>
                    <SortableTh label="Joined" sortKey="joined" currentKey={sortKey} asc={sortAsc} onClick={() => toggleSort('joined')} data-id="element-1329" />
                    <SortableTh label="Lifetime" sortKey="spend" currentKey={sortKey} asc={sortAsc} onClick={() => toggleSort('spend')} align="right" data-id="element-1330" />
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1331">
                      Tags
                    </th>
                    <th className="px-4 py-3 w-10" data-id="element-1332" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-1333">
                  {filtered.length === 0 && <tr data-id="element-1334">
                      <td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm" data-id="element-1335">
                        No customers match these filters.
                      </td>
                    </tr>}
                  {filtered.map((c, idx) => <CustomerRow key={c.id} customer={c} index={idx + 1} onClick={() => setSelectedId(c.id)} data-id="element-1336" />)}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500" data-id="element-1337">
              <span data-id="element-1338">
                Showing {filtered.length} of {customers.length} customers
              </span>
              <span className="text-gray-400" data-id="element-1339">
                Tap a row to view full profile
              </span>
            </div>
          </div>
        </div>
      </main>

      {/* Customer profile drawer */}
      <CustomerProfilePanel customer={selectedCustomer || null} onClose={() => setSelectedId(null)} branches={branches} data-id="element-1340" />
    </div>;
}
function StatCard({
  icon,
  label,
  value,
  tint
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tint: 'orange' | 'blue' | 'emerald' | 'purple' | 'pink';
}) {
  const colors: Record<string, string> = {
    orange: 'bg-orange-100 text-orange-600',
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-purple-100 text-purple-600',
    pink: 'bg-pink-100 text-pink-600'
  };
  return <div className="rounded-xl border border-gray-200 bg-white p-3.5" data-id="element-1341">
      <div className="flex items-center gap-2 mb-2" data-id="element-1342">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${colors[tint]}`} data-id="element-1343">
          {icon}
        </div>
        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide" data-id="element-1344">
          {label}
        </span>
      </div>
      <div className="text-xl font-bold text-gray-900 tabular-nums" data-id="element-1345">
        {value}
      </div>
    </div>;
}
function SortableTh({
  label,
  sortKey,
  currentKey,
  asc,
  onClick,
  align
}: {
  label: string;
  sortKey: string;
  currentKey: string;
  asc: boolean;
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  const isActive = currentKey === sortKey;
  return <th onClick={onClick} className={`px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-1346">
      <span className="inline-flex items-center gap-1" data-id="element-1347">
        {label}
        {isActive && <span className="text-orange-500" data-id="element-1348">{asc ? '↑' : '↓'}</span>}
      </span>
    </th>;
}
function CustomerRow({
  customer,
  index,
  onClick
}: {
  customer: Customer;
  index: number;
  onClick: () => void;
}) {
  const m = getCustomerMetrics(customer);
  const initials = (customer.firstName[0] || '') + (customer.lastName[0] || '');
  const joinedDate = new Date(customer.joined);
  const formatDate = (iso: string | undefined) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };
  const sexLabel: Record<Sex, {
    label: string;
    color: string;
  }> = {
    male: {
      label: 'MALE',
      color: 'text-blue-600'
    },
    female: {
      label: 'FEMALE',
      color: 'text-pink-600'
    },
    unspecified: {
      label: '—',
      color: 'text-gray-400'
    }
  };
  return <tr onClick={onClick} className="hover:bg-orange-50/40 cursor-pointer transition-colors group" data-id="element-1349">
      <td className="px-4 py-3 text-gray-400 text-xs tabular-nums" data-id="element-1350">{index}</td>
      <td className="px-4 py-3" data-id="element-1351">
        <div className="flex items-center gap-3" data-id="element-1352">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 text-orange-700 flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-1353">
            {initials.toUpperCase()}
          </div>
          <div className="min-w-0" data-id="element-1354">
            <div className="font-semibold text-gray-900 truncate" data-id="element-1355">
              {customer.firstName} {customer.lastName}
            </div>
            {customer.email && <div className="text-[11px] text-gray-500 truncate" data-id="element-1356">
                {customer.email}
              </div>}
          </div>
        </div>
      </td>
      <td className={`px-4 py-3 text-xs font-bold ${sexLabel[customer.sex].color}`} data-id="element-1357">
        {sexLabel[customer.sex].label}
      </td>
      <td className="px-4 py-3 text-gray-700 tabular-nums text-xs" data-id="element-1358">
        {customer.phone}
      </td>
      <td className="px-4 py-3 text-gray-700 tabular-nums text-xs" data-id="element-1359">
        {formatDate(customer.birthDate)}
      </td>
      <td className="px-4 py-3 text-gray-700 text-xs" data-id="element-1360">
        <div data-id="element-1361">{formatDate(customer.joined)}</div>
        <div className="text-[10px] text-gray-400" data-id="element-1362">
          {joinedDate.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })}
        </div>
      </td>
      <td className="px-4 py-3 text-right" data-id="element-1363">
        <div className="font-bold text-gray-900 tabular-nums" data-id="element-1364">
          ETB {Math.round(m.lifetimeSpend).toLocaleString()}
        </div>
        <div className="text-[10px] text-gray-400" data-id="element-1365">
          {m.totalOrders} order{m.totalOrders === 1 ? '' : 's'}
        </div>
      </td>
      <td className="px-4 py-3" data-id="element-1366">
        <div className="flex items-center gap-1 flex-wrap" data-id="element-1367">
          {customer.tags.slice(0, 2).map(tag => <TagPill key={tag} tag={tag} data-id="element-1368" />)}
          {customer.tags.length > 2 && <span className="text-[10px] text-gray-400 font-semibold" data-id="element-1369">
              +{customer.tags.length - 2}
            </span>}
        </div>
      </td>
      <td className="px-4 py-3" data-id="element-1370">
        <ChevronRight size={15} className="text-gray-300 group-hover:text-orange-500 transition-colors" data-id="element-1371" />
      </td>
    </tr>;
}
export function TagPill({
  tag
}: {
  tag: CustomerTag;
}) {
  const meta = tagMeta[tag];
  const colorClasses: Record<string, string> = {
    amber: 'bg-amber-100 text-amber-800',
    emerald: 'bg-emerald-100 text-emerald-700',
    blue: 'bg-blue-100 text-blue-700',
    gray: 'bg-gray-200 text-gray-700',
    pink: 'bg-pink-100 text-pink-700'
  };
  const Icon: Record<string, ComponentType<{
    size?: number;
  }>> = {
    crown: Crown,
    repeat: Repeat,
    sparkles: Sparkles,
    moon: Moon,
    cake: Cake
  };
  const I = Icon[meta.icon];
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${colorClasses[meta.color]}`} data-id="element-1372">
      {I && <I size={9} data-id="element-1373" />}
      {meta.label}
    </span>;
}